import { html, unsafeCSS } from 'lit';
import { property, state } from 'lit/decorators.js';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafModal } from '@cigna/dcws_leaf-web-components/components/modal/modal.component.js';
import { CHCPhoneNumber } from '@cigna/dcws_leaf-web-components/chc-components/phone-number/phone-number.component.js';
import { I18nMixin } from '../../utils/i18n-mixin.js';
import styles from './medicare-ntm-campaign-modal.scss?inline';
import type { NtmCampaignModalData, Message, Footnote, Pdf } from './interfaces.js';

export class CHCappMedicareNtmCampaignModal extends ScopedRegistryHost(I18nMixin(LeafElement)) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-modal': LeafModal,
    'chc-phone-number': CHCPhoneNumber,
  };
  private campaignModalData?: NtmCampaignModalData;
  protected override ns = 'medicare-ntm-campaign-modal';

  @state()
  isModalOpen: boolean = false;

  @state()
  messageData!: Message | undefined;

  @state()
  footNoteData!: Footnote | undefined;

  @state()
  pdfData!: Pdf | undefined;

  @property({ reflect: true })
  touches: string | undefined;

  @property({ reflect: true })
  hash: string | undefined;

  @state()
  get touch() {
    return new URLSearchParams(window.location.search).get('Touch');
  }

  @state()
  get state() {
    return new URLSearchParams(window.location.search).get('state');
  }

  @state()
  get tfn() {
    const tfn = new URLSearchParams(window.location.search).get('tfn');
    const validTfn = tfn?.match(/^(\d{10})$/);
    return validTfn && validTfn !== null ? tfn : '8009971654';
  }

  override onNamespacesLoaded = () => {
    this.campaignModalData = this.i18n?.getLanguageData(this.ns) as NtmCampaignModalData;
    this.validateSearchParams();
  };

  private updateModalContent() {
    if (this.touch) {
      this.messageData = this.campaignModalData?.messages.find((message: Message) => message.touch === this.touch);
      this.footNoteData = this.campaignModalData?.footnotes.find(
        (footnote: Footnote) =>
          footnote.touch === this.touch && footnote.state?.toLowerCase() === this.state?.toLowerCase(),
      );
      this.pdfData = this.campaignModalData?.pdfs.find(
        (pdf: Pdf) => pdf.state?.toLowerCase() === this.state?.toLowerCase(),
      );
    }
  }

  private async validateSearchParams() {
    const hash = window.location.hash;
    if (
      this.touches &&
      this.touch &&
      this.touches.includes(this.touch) &&
      (!this.hash || (this.hash && this.hash === hash))
    ) {
      this.updateModalContent();
      await this.updateComplete;
      this.isModalOpen = !this.isModalOpen;
    }
  }

  private renderMessageContent() {
    const pdfLink = `${this.pdfData?.pdfUrl ? this.pdfData?.pdfUrl : this.messageData?.downloadLink}`;
    if (this.messageData) {
      return html`
        <h4>${this.messageData?.header}</h4>
        <p>
          ${this.messageData?.info}
          <a href="${pdfLink}" target="_blank">${this.messageData?.downloadText}</a>
        </p>
      `;
    }
    return html``;
  }

  private renderFooterContent() {
    if (this.footNoteData) {
      return html`
        <div class="footer-content">
          <span>${this.footNoteData?.catId}</span>
          <span class="footer-right">${this.footNoteData?.matId}</span>
        </div>
      `;
    }
    return html``;
  }

  override render() {
    const loadingOrError = this.renderLoadingOrError();
    return html`
      <leaf-modal
        class="chc-app-medicare-ntm-campaign-modal"
        id="ntmcampaign-modal"
        size="lg"
        ?open=${this.isModalOpen ? this.isModalOpen : undefined}
      >
        ${loadingOrError
          ? loadingOrError
          : html`
              <h3 slot="heading">${this.campaignModalData?.misc.success}</h3>
              <div>
                ${this.renderMessageContent()}
                <p>
                  ${this.campaignModalData?.misc.callText}
                  <chc-phone-number number="${this.tfn ?? ''}" separator="-" parentheses></chc-phone-number>
                  ${this.campaignModalData?.misc.ttyText}<br />
                  ${this.campaignModalData?.misc.availability}
                </p>
                ${this.renderFooterContent()}
              </div>
            `}
      </leaf-modal>
    `;
  }
}
